import { ContactAvatar } from '@poseidon/components/Avatar';
import { Button } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import { faceName, type SequencePersonIdentity } from '~/components/Sequence/SequenceNodeFaces';

interface Props {
  /**
   * The contact this graph is about. Omit when the host doesn't have their name/avatar to hand — the
   * gutter still renders whatever else belongs on the row (never gate a control on a presentational prop).
   */
  contact?: SequencePersonIdentity;
  /**
   * When this contact is parked on the step's call/reminder to-do: complete it and advance the sequence
   * past the manual step. Omit → face only (not a parked manual task, so there'd be nothing to do).
   */
  onMarkDone?: () => void;
}

/**
 * The left-gutter row on the LOCKED per-contact sequence graph, beside the step the contact is currently
 * on: "this contact is here" + the action available there. The builder's graph fills the same gutter with
 * the team's people-piles ({@link SequenceNodeFaces}) — a per-contact graph is about exactly one person, so
 * it shows exactly one face. Face and control share ONE flex row so they can never overlap each other; with
 * neither to show, nothing renders.
 */
export function SequenceContactHere({ contact, onMarkDone }: Props) {
  if (contact == null && onMarkDone == null) return null;
  return (
    <div className="flex items-center gap-1.5">
      {contact != null && (
        <ContactAvatar
          firstName={contact.firstName}
          lastName={contact.lastName}
          imageSrc={contact.imageSrc}
          shape="circle"
          tooltip={`${faceName(contact, 'This contact')} is here`}
          // Ringed like the node itself is ringed (`current`), so the marker and the step read as one unit.
          className="size-6 rounded-full ring-2 ring-blue-400 ring-offset-1 ring-offset-slate-50 dark:ring-offset-neutral-900"
        />
      )}
      {onMarkDone != null && (
        <Button
          size="2xs"
          color="green"
          tone="soft"
          icon={<General.CheckCircle />}
          tooltip="Mark done — complete this to-do and advance the sequence"
          onClick={onMarkDone}
          className="whitespace-nowrap">
          Mark done
        </Button>
      )}
    </div>
  );
}
